import { Menu as MenuPrimitive } from "@base-ui/react/menu";

import { cn } from "@lib/utils";

/** Displays a list of actions or options that a user can choose */
const DropdownMenu = ({ ...props }: MenuPrimitive.Root.Props) => (
	<MenuPrimitive.Root data-slot="dropdown-menu" {...props} />
);

/** Dropdown menu trigger element
 * @prop Trigger - The trigger of the dropdown menu
 */
const DropdownMenuTrigger = ({ ...props }: MenuPrimitive.Trigger.Props) => (
	<MenuPrimitive.Trigger
		// nativeButton={true}
		className="cursor-pointer border-none outline-none"
		data-slot="dropdown-menu-trigger"
		{...props}
	/>
);

/** Dropdown menu content element
 * @prop align - The alignment of the dropdown menu
 * @prop alignOffset - The offset of the dropdown menu
 * @prop side - The side of the dropdown menu
 * @prop sideOffset - The offset of the dropdown menu
 * @prop className - The class name to apply to the dropdown menu *
 */
const DropdownMenuContent = ({
	align = "start",
	alignOffset = 0,
	side = "bottom",
	sideOffset = 4,
	className,
	...props
}: MenuPrimitive.Popup.Props &
	Pick<MenuPrimitive.Positioner.Props, "align" | "alignOffset" | "side" | "sideOffset">) => (
	<MenuPrimitive.Portal>
		<MenuPrimitive.Positioner
			className="isolate z-sticky outline-none"
			align={align}
			alignOffset={alignOffset}
			side={side}
			sideOffset={sideOffset}
		>
			<MenuPrimitive.Popup
				data-slot="dropdown-menu-content"
				className={cn(
					`ring-foreground/10 bg-popover text-popover-foreground z-sticky max-h-(--available-height) w-(--anchor-width)
					min-w-32 origin-(--transform-origin) overflow-x-hidden overflow-y-auto shadow-md duration-100 outline-none
					data-closed:animate-out data-closed:overflow-hidden data-closed:fade-out-0 data-closed:zoom-out-95
					data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2
					data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2
					data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2
					data-[side=top]:slide-in-from-bottom-2`,
					"transition- rounded-lg bg-block shadow-[0_2px_20px_0_rgba(0,0,0,0.90)]",
					className,
				)}
				{...props}
			/>
		</MenuPrimitive.Positioner>
	</MenuPrimitive.Portal>
);

/** Dropdown menu item element
 * @prop className - The class name to apply to the dropdown menu item
 * @prop inset - Whether to inset the dropdown menu item
 * @prop variant - The variant of the dropdown menu item
 */
const DropdownMenuItem = ({
	className,
	inset,
	variant = "default",
	...props
}: MenuPrimitive.Item.Props & {
	inset?: boolean;
	variant?: "default" | "destructive";
}) => (
	<MenuPrimitive.Item
		data-slot="dropdown-menu-item"
		data-inset={inset}
		data-variant={variant}
		className={cn(
			`focus:bg-accent focus:text-accent-foreground data-[variant=destructive]:text-destructive
			data-[variant=destructive]:focus:bg-destructive/10 dark:data-[variant=destructive]:focus:bg-destructive/20
			data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:text-destructive
			not-data-[variant=destructive]:focus:**:text-accent-foreground group/dropdown-menu-item relative flex
			cursor-default items-center gap-1.5 text-sm outline-hidden select-none data-disabled:pointer-events-none
			data-disabled:opacity-50 data-inset:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0
			[&_svg:not([class*='size-'])]:size-4`,
			"cursor-pointer bg-block p-2.5 hover:bg-ground-hover",
			className,
		)}
		{...props}
	/>
);

DropdownMenu.displayName = "DropdownMenu";
DropdownMenuTrigger.displayName = "DropdownMenuTrigger";
DropdownMenuContent.displayName = "DropdownMenuContent";
DropdownMenuItem.displayName = "DropdownMenuItem";

export { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem };
